import React, { useRef, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Pen, Upload, Trash2, Save, Eraser, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SignatureSettings = () => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [mode, setMode] = useState("draw"); // draw | upload
  const [current, setCurrent] = useState(null); // existing saved signature dataUri
  const [preview, setPreview] = useState(null); // new upload preview dataUri
  const [hasDrawn, setHasDrawn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadExisting = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/broker/signature`);
      setCurrent(res.data?.hasSignature ? res.data.dataUri : null);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const setupCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  useEffect(() => { if (mode === "draw") setupCanvas(); }, [mode, setupCanvas]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
  const end = () => { drawing.current = false; };

  const clearCanvas = () => { setupCanvas(); setHasDrawn(false); };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) { toast.error("Doar PNG/JPG/WebP"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("Imaginea depășește 3MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    let imageData = null;
    if (mode === "draw") {
      if (!hasDrawn) { toast.error("Desenează semnătura mai întâi"); return; }
      imageData = canvasRef.current.toDataURL("image/png");
    } else {
      if (!preview) { toast.error("Încarcă o imagine mai întâi"); return; }
      imageData = preview;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/broker/signature`, { imageData });
      toast.success("Semnătură salvată — va apărea pe documentele generate");
      setPreview(null); setHasDrawn(false);
      await loadExisting();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Ștergi semnătura salvată?")) return;
    try {
      await axios.delete(`${API_URL}/api/broker/signature`);
      setCurrent(null);
      toast.success("Semnătură ștearsă");
    } catch (e) { toast.error("Eroare la ștergere"); }
  };

  return (
    <Card data-testid="signature-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Pen className="h-4 w-4" /> Semnătura ta (apare automat pe PDF-uri)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center text-gray-500 text-sm"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Se încarcă...</div>
        ) : (
          <>
            {current && (
              <div className="flex items-center justify-between rounded-lg border bg-emerald-50 border-emerald-200 p-3" data-testid="signature-current">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Semnătură setată</p>
                    <img src={current} alt="semnătură curentă" className="mt-1 max-h-16 bg-white border rounded px-2" />
                  </div>
                </div>
                <Button variant="outline" size="sm" className="text-red-600" onClick={remove} data-testid="signature-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Șterge
                </Button>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant={mode === "draw" ? "default" : "outline"} size="sm" onClick={() => setMode("draw")} data-testid="signature-mode-draw">
                <Pen className="h-4 w-4 mr-1" /> Desenează
              </Button>
              <Button variant={mode === "upload" ? "default" : "outline"} size="sm" onClick={() => setMode("upload")} data-testid="signature-mode-upload">
                <Upload className="h-4 w-4 mr-1" /> Încarcă imagine
              </Button>
            </div>

            {mode === "draw" ? (
              <div>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={180}
                  data-testid="signature-canvas"
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none cursor-crosshair"
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-400">Desenează cu mouse-ul sau degetul.</p>
                  <Button variant="ghost" size="sm" onClick={clearCanvas} data-testid="signature-clear-btn">
                    <Eraser className="h-4 w-4 mr-1" /> Șterge desenul
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} data-testid="signature-upload-input"
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer" />
                {preview && <img src={preview} alt="previzualizare" className="mt-3 max-h-24 bg-white border rounded px-2" data-testid="signature-upload-preview" />}
                <p className="text-xs text-gray-400 mt-1">PNG transparent recomandat. Max 3MB.</p>
              </div>
            )}

            <Button onClick={save} disabled={saving} data-testid="signature-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salvează semnătura
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SignatureSettings;
